import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map, catchError, first } from 'rxjs/operators';
import { of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { RestEndpoint } from '../../../constants/rest-endpoint.constants';

export interface GeolocationData {
    ip: string;
    latitude?: number;
    longitude?: number;
    city?: string;
    country?: string;
    region?: string;
    timezone?: string;
}

@Injectable({
    providedIn: 'root'
})
export class GeolocationService {
    private cachedData: GeolocationData | null = null;

    constructor(private readonly http: HttpClient) { }

    /**
     * Get user's IP address and location data
     * Uses multiple fallback services for reliability
     */
    public getLocationData(): Observable<GeolocationData> {
        if (this.cachedData) {
            return of(this.cachedData);
        }

        return from(this.fetchLocationData()).pipe(
            map(data => {
                this.cachedData = data;
                return data;
            }),
            catchError(error => {
                console.error('Error fetching location data:', error);
                // Return minimal data with IP from request headers if available
                return of({
                    ip: 'unknown',
                    latitude: undefined,
                    longitude: undefined
                });
            })
        );
    }

    private async fetchLocationData(): Promise<GeolocationData> {
        try {
            // Try ipapi.co first (free, no API key needed)
            const response = await fetch('https://ipapi.co/json/');
            if (response.ok) {
                const data = await response.json();
                return {
                    ip: data.ip || 'unknown',
                    latitude: data.latitude,
                    longitude: data.longitude,
                    city: data.city,
                    country: data.country,
                    region: data.region,
                    timezone: data.timezone
                };
            }
        } catch (error) {
            console.warn('ipapi.co failed, trying fallback...', error);
        }

        try {
            // Fallback to ip-api.com
            const response = await fetch('http://ip-api.com/json/');
            if (response.ok) {
                const data = await response.json();
                return {
                    ip: data.query || 'unknown',
                    latitude: data.lat,
                    longitude: data.lon,
                    city: data.city,
                    country: data.countryCode,
                    region: data.regionName,
                    timezone: data.timezone
                };
            }
        } catch (error) {
            console.warn('ip-api.com failed, trying last fallback...', error);
        }

        // Last fallback - get IP only
        try {
            const response = await fetch('https://api.ipify.org?format=json');
            if (response.ok) {
                const data = await response.json();
                return {
                    ip: data.ip || 'unknown',
                    latitude: undefined,
                    longitude: undefined
                };
            }
        } catch (error) {
            console.error('All geolocation services failed', error);
        }

        throw new Error('Unable to fetch location data');
    }

    /**
     * Alias for getLocationData (for compatibility)
     */
    public getGeolocationData(): Observable<GeolocationData> {
        return this.getLocationData();
    }

    /**
     * Clear cached data (useful for testing or when user changes location)
     */
    public clearCache(): void {
        this.cachedData = null;
    }
}
